"use client"
import {
  addDoc,
  collection,
  doc,
  limit,
  getDoc,
  DocumentReference,
  getDocs,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  Unsubscribe,
  where,
  documentId,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  deleteDoc,
  CollectionReference,
  writeBatch,
  orderBy
} from "@firebase/firestore"

import { httpsCallable } from "firebase/functions"
import { useHelper } from "./useHelper"

import { useEffect, useState } from "react"
import ListNews from "@entities/listNews"
import NewsInfo from "@entities/newsInfo"
import ItemInfo from "@entities/itemInfo"
import ItemRunInfo from "@/functions/src/entities/itemRunInfo"
import ProfitDotComTickerQuote from '@entities/profitDotComTickerQuote'
import ListInfo from "@/functions/src/entities/listInfo"
import { app, db, functions } from "@/hooks/firebase.client.config"
import UserInfo2 from "@/functions/src/entities/userInfo2"
import { EnumCollections } from "@/functions/src/entities/enumCollections"
import DateTimeHelper from "@/functions/src/date-time-helper"
import { auth } from "@/hooks/firebase.client.config"
import { EnumBatchType } from "@/functions/src/entities/enumBatchType"
import AIInstruction from "@/functions/src/entities/aiInstruction";
import NewsSourceInfo from "@/functions/src/entities/newsSourceInfo";
import NewsTagInfo from "@/functions/src/entities/newsTagInfo";
import BatchRunInfo from "@/functions/src/entities/batchRunInfo";
import {
  COLL_NEWS,
  COLL_ITEMS,
  COLL_ITEMS_RUNS,
  COLL_LISTS,
  COLL_USERS,
  COLL_BATCH_RUNS,
  COLL_LIST_RUNS,
  COLL_VISITORS,
  COLL_MARKET_NEWS,
  COLL_PROMPTS,
  COLL_NEWS_SOURCES,
  COLL_NEWS_TAGS,
  BATCH_TIMEOUT
} from "@/functions/src/constants"



export function useFirebaseService() {

  // Helper function to enrich user info with Stripe and subscription data
  const enrichUserInfo = async (basicUser: UserInfo2): Promise<UserInfo2> => {
    try {
      // Step 1: Enrich with Stripe customer data
      const stripeEnrichedUser = await confirmCustomer(basicUser);
      if (!stripeEnrichedUser) {
        return basicUser;
      }

      // Step 2: Enrich with subscription status
      // console.log('Step 2: Getting subscription status');
      // const hasActiveSub = await hasActiveSubscription(stripeEnrichedUser.uid);
      // stripeEnrichedUser.hasActiveSubscription = hasActiveSub;
      // console.log('Enrichment complete:', {
      //   email: stripeEnrichedUser.email,
      //   stripeId: stripeEnrichedUser.stripeCustomerId,
      //   hasSubscription: stripeEnrichedUser.hasActiveSubscription
      // });

      return stripeEnrichedUser;
    } catch (error) {
      console.error('Error during user info enrichment:', error);
      return basicUser; // Fallback to basic user info if enrichment fails
    }
  };

  const { compareDates } = useHelper();
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isSubscribed, setIsSubscribed] = useState(false)  // Always false for now until auth is implemented

  useEffect(() => {
    try {
      // Check if Firebase app is available
      if (!app?.options) {
        setError(new Error('Firebase app is not properly configured'))
        return
      }

      // Check if Firestore is available
      if (!db) {
        setError(new Error('Firestore is not properly configured'))
        return
      }

      // If we get here, Firebase is properly initialized
      setIsInitialized(true)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Firebase initialization failed'))
    }
  }, [])

  useEffect(() => {
    setIsSubscribed(false);
  }, []);

  function docSnapshotToDataWithId(docSnap: QueryDocumentSnapshot) {
    return {
      id: docSnap.id,
      ...docSnap.data()
    }
  }

  function querySnapshotToDataWithId(qSnapshot: any) {
    return (qSnapshot.docs || []).map((doc: any) => docSnapshotToDataWithId(doc));
  }

  function getSnapshot(collectionName: string, callback: any): Unsubscribe {
    const collRef: CollectionReference = collection(db, collectionName);
    return onSnapshot(collRef, callback);
  }

  async function getUserByEmail(email: string): Promise<UserInfo2 | null> {
    const q = query(collection(db, COLL_USERS), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const retVal = querySnapshot.docs[0].data() as UserInfo2
    // console.log("==========================================");
    // console.log(`Current User Data at getUserByEmail: ${JSON.stringify(retVal)}`);
    // console.log("==========================================");
    return retVal;
  }

  async function getUserById(userId: string): Promise<UserInfo2 | null> {
    try {
      const userRef = doc(db, COLL_USERS, userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) return null;
      return userDoc.data() as UserInfo2;
    } catch (error) {
      console.error(`Error fetching user by ID ${userId}:`, error);
      return null;
    }
  }

  async function getCustomerByEmail(email: string): Promise<{ exists: boolean, hasStripeCustomer: boolean }> {
    try {
      const getCustomerByEmailFn = httpsCallable<{ email: string }, { exists: boolean, hasStripeCustomer: boolean }>(functions, 'getCustomerByEmail');
      const result = await getCustomerByEmailFn({ email });
      return result.data;
    } catch (error) {
      console.error('Error checking customer by email:', error);
      // Default to not existing if there's an error
      return { exists: false, hasStripeCustomer: false };
    }
  }

  const getItemDoc = (id: string) => {
    if (!id) return null;
    return doc(db, COLL_ITEMS, id);
  }

  const getNewsDoc = async (symbol: string) => {
    const docs = await getDocumentsByField(COLL_NEWS, "symbol", symbol);
    return docs.length > 0 ? docs[0] : null;
  }

  const getDocumentsByField = async (collectionName: string, field: string, value: any): Promise<any[]> => {
    let qry = query(collection(db, collectionName), where(field, "==", value));
    const docs = (await getDocs(qry)).docs;
    return docs.map(doc => doc.data());
  };

  const getNewsItemSnapshot = (
    symbol: string,
    onData: (data: any) => void,
    onError: (error: any) => void
  ): Unsubscribe => {
    if (!symbol) {
      console.error('Symbol is required for getNewsItemSnapshot');
      return () => { };
    }

    // console.log('getNewsItemSnapshot called for symbol:', symbol);
    const newsRef = doc(db, COLL_NEWS, symbol.toUpperCase());
    return onSnapshot(
      newsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // console.log(`News data for ${symbol}:`, data);
          if (data) {
            onData({
              id: snapshot.id,
              symbol: symbol.toUpperCase(),
              ...data
            });
          } else {
            onData(createDefaultNewsItem(symbol, symbol));
          }
        } else {
          onData(createDefaultNewsItem(symbol, symbol));
        }
      },
      onError
    );
  }

  const getItemsSnapshot = (items: string[], handleData: (doc: any) => void, handleError: (error: any) => void): Unsubscribe => {
    // console.log('getItemsSnapshot called for items:', items);
    try {
      if (!db) throw new Error('Firebase is not configured');
      if (!items || items.length === 0) {
        handleData([]);
        return () => { }; // Return empty unsubscribe function if no items
      }

      // Create a query that matches any document whose ID is in the items array
      const q = query(
        collection(db, COLL_ITEMS),
        where(documentId(), 'in', items)
      );

      // Set up the snapshot listener
      return onSnapshot(
        q,
        (snapshot) => {
          const documents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          handleData(documents);
        },
        handleError
      );
    } catch (error) {
      console.error('Error in getItemsSnapshot:', error);
      handleError(error);
      return () => { }; // Return empty unsubscribe function on error
    }
  }

  const addNews = async (): Promise<DocumentReference | null> => {
    try {
      const docRef = await addDoc(collection(db, COLL_NEWS), {
        name: "Tokyo",
        country: "Japan"
      });

      // Document created successfully
      return docRef;

    } catch (error) {
      console.error("Error adding document: ", error);
      return null;
    }
  }

  const updateVisitorCount = async (ip: string): Promise<void> => {
    const today = new Date().toISOString().split('T')[0];
    const visitorRef = doc(db, COLL_VISITORS, today);

    try {
      const docSnap = await getDoc(visitorRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (!data.ips.includes(ip)) {
          await updateDoc(visitorRef, {
            count: data.count + 1,
            ips: arrayUnion(ip)
          });
        }
      } else {
        await setDoc(visitorRef, {
          date: today,
          count: 1,
          ips: [ip]
        });
      }
    } catch (error) {
      console.error("Error updating visitor count:", error);
    }
  };

  const getVisitorCountSnapshot = (
    onData: (count: number) => void,
    onError: (error: Error) => void
  ): Unsubscribe => {
    const today = new Date().toISOString().split('T')[0];
    const visitorRef = doc(db, COLL_VISITORS, today);

    return onSnapshot(
      visitorRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          onData(data.count || 0);
        } else {
          onData(0);
        }
      },
      onError
    );
  };

  async function getListData(listId: string): Promise<any> {
    const listRef = doc(db, COLL_LISTS, listId);
    const listDoc = await getDoc(listRef);
    return listDoc.data() || {};
  }

  async function getListNewsInfo(listId: string): Promise<ListNews> {
    const defaultList = [createDefaultNewsItem()];

    try {
      // Step 1: Get list data
      const listData = await getListData(listId);
      const itemIds = listData.items || [];
      const listName = listData.name || "";

      // If no items in list, return early with default
      if (!itemIds.length) {
        return new ListNews(listId, listName, defaultList);
      }

      // Step 2: Get item information for all items
      const itemInfos: ItemInfo[] = [];
      try {
        const itemsSnapshot = await getDocs(
          query(collection(db, COLL_ITEMS),
            where(documentId(), 'in', itemIds))
        );

        itemsSnapshot.forEach(doc => {
          const data = doc.data();
          if (data && data.symbol) {  // Only add items with valid symbols
            itemInfos.push(data as ItemInfo);
          }
        });
      } catch (error) {
        console.error('Error fetching item information:', error);
        return new ListNews(listId, listName, defaultList);
      }

      // If no valid items found, return early with default
      if (!itemInfos.length) {
        return new ListNews(listId, listName, defaultList);
      }

      // Step 3: Extract and filter valid symbols
      const symbols = itemInfos
        .map(item => item.symbol)
        .filter((symbol): symbol is string => !!symbol);

      if (!symbols.length) {
        return new ListNews(listId, listName, defaultList);
      }

      // Step 4: Get news items for all symbols
      let newsItems: NewsInfo[] = [];
      try {
        const newsSnapshot = await getDocs(
          query(collection(db, COLL_NEWS),
            where(documentId(), 'in', symbols))
        );

        newsItems = newsSnapshot.docs
          .map(doc => NewsInfo.create(doc.id, doc.data()))
          .filter(item => item.long || item.short); // Only include items with content
      } catch (error) {
        console.error('Error fetching news items:', error);
        return new ListNews(listId, listName, defaultList);
      }

      // Return with default list if no valid news items found
      return new ListNews(
        listId,
        listName,
        newsItems.length ? newsItems : defaultList
      );

    } catch (error) {
      console.error('Error in getListNewsInfo:', error);
      return new ListNews(listId, "", []);
    }
  }

  const getMultipleListsNewsInfo = async (listIds: string[]): Promise<ListNews[]> => {
    const results: ListNews[] = [];
    for (const listId of listIds) {
      const listNews = await getListNewsInfo(listId);
      results.push(listNews);
    }
    return results;
  }

  const getListsForUser = async (userId: string): Promise<string[]> => {
    try {
      if (!db) throw new Error('Firebase is not configured');

      const userDocRef = doc(db, COLL_USERS, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          uid: userId,
          listIds: []
        });
        return [];
      }

      const userData = userDoc.data();
      return userData?.listIds || [];
    } catch (error) {
      console.error('Error in getListsForUser:', error);
      throw error;
    }
  };

  /**
   * Get the latest batch ID for a user
   * @param userId The user ID to get the batch ID for
   * @returns Promise<string | null> The current batch ID or null if not set
   */
  const getLatestBatchId = async (userId: string): Promise<string | null> => {
    try {
      if (!db) throw new Error('Firebase is not configured');

      const userRef = doc(db, COLL_USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // User not found
        return null;
      }

      const userData = userDoc.data();
      return userData?.currentBatchId || null;
    } catch (error) {
      console.error(`Error in getLatestBatchId for user ${userId}:`, error);
      return null;
    }
  }

  /**
   * Get the latest primary batch ID from the batchruns collection
   * This is used for non-authenticated users to get the most recent primary batch (batchType == 0)
   * @returns Promise<string | null> The latest primary batch ID or null if not found
   */
  const getLatestPrimaryBatchId = async (): Promise<string | null> => {
    try {
      if (!db) throw new Error('Firebase is not configured');

      // Query batchruns collection for primary batches (batchType == 0) ordered by createdOn descending
      const batchRunsQuery = query(
        collection(db, COLL_BATCH_RUNS),
        where('batchType', '==', 0),
        orderBy('createdOn', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(batchRunsQuery);

      if (querySnapshot.empty) {
        console.log('No primary batch runs found');
        return null;
      }

      const latestBatch = querySnapshot.docs[0];
      return latestBatch.id;
    } catch (error) {
      console.error('Error getting latest primary batch ID from batchruns:', error);
      return null;
    }
  }

  /**
   * Get the complete BatchRunInfo object for a given batch ID
   * @param batchId The batch ID to fetch
   * @returns Promise<BatchRunInfo | null> The complete BatchRunInfo object or null if not found
   */
  const getBatchRunInfo = async (batchId: string): Promise<BatchRunInfo | null> => {
    try {
      if (!db) throw new Error('Firebase is not configured');
      if (!batchId) return null;

      const batchRef = doc(db, COLL_BATCH_RUNS, batchId);
      const batchDoc = await getDoc(batchRef);

      if (!batchDoc.exists()) {
        console.log(`Batch run ${batchId} not found`);
        return null;
      }

      return batchDoc.data() as BatchRunInfo;
    } catch (error) {
      console.error(`Error fetching BatchRunInfo for ${batchId}:`, error);
      return null;
    }
  }


  /**
   * Add a new watchlist for a user
   * @param userId - The user ID
   * @param listName - The name of the watchlist
   * @param tickers - Optional array of tickers to add to the watchlist
   * @returns Promise with the list ID
   */
  const addUserList = async (userId: string, listName: string, tickers: string[] = []): Promise<string> => {
    if (!listName.trim()) {
      throw new Error("List name cannot be empty")
    }

    try {
      // Create the new list
      const newList = {
        name: listName.trim(),
        items: [],
        tickers: tickers, // Add tickers to the list
        userId,
        createdOn: serverTimestamp(),
        updatedOn: serverTimestamp()
      }
      const listRef = await addDoc(collection(db, COLL_LISTS), newList)

      // Add the list reference to the user's listIds (not 'lists')
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        listIds: arrayUnion(listRef.id)
      })

      return listRef.id
    } catch (error) {
      console.error('Error adding list:', error)
      throw error
    }
  }

  const deleteList = async (userId: string, listId: string): Promise<void> => {
    if (!listId) {
      throw new Error("List ID is required")
    }

    try {
      // First, remove the list from user's listIds array
      const userRef = doc(db, COLL_USERS, userId)
      await updateDoc(userRef, {
        listIds: arrayRemove(listId)
      })

      // Then delete the list document
      const listRef = doc(db, COLL_LISTS, listId)
      await deleteDoc(listRef)
    } catch (error) {
      console.error('Error deleting list:', error)
      throw error
    }
  }

  function createDefaultNewsItem(itemId: string = 'default', symbol: string = ''): NewsInfo {
    const now = new Date();
    const newsItem = new NewsInfo(itemId, itemId);
    newsItem.symbol = symbol;
    newsItem.long = `No News for: ${symbol || 'Unknown Ticker'}`;
    newsItem.short = `No News for: ${symbol || 'Unknown Ticker'}`;
    newsItem.articles = [];
    newsItem.isArticlesUpdated = false;
    newsItem.createdOn = now;
    newsItem.modifiedOn = now;
    newsItem.refreshedOnL = now.getTime();
    return newsItem;
  }

  const cleanup = async () => {
    // Add any cleanup logic here if needed
    // This is a no-op for now since we're using one-time fetches
    return Promise.resolve();
  }

  const addItemToWatchlist = async (listId: string, quoteData: ProfitDotComTickerQuote): Promise<string> => {
    try {
      if (!db) throw new Error('Firebase is not configured')
      if (!quoteData?.symbol) throw new Error('Symbol is required')
      if (!listId) throw new Error('List ID is required')

      const symbol = quoteData.symbol.toUpperCase()
      const itemToSave = ProfitDotComTickerQuote.toItemInfo(quoteData)
      const now = new Date()

      // Use symbol as the document ID
      const itemRef = doc(db, COLL_ITEMS, symbol)
      const itemDoc = await getDoc(itemRef)

      if (!itemDoc.exists()) {
        // Create new item with symbol as ID
        await setDoc(itemRef, {
          ...itemToSave,
          createdOn: now
        })
      } else {
        // Update existing item
        await updateDoc(itemRef, {
          ...itemToSave,
          modifiedOn: now
        })
      }

      // Now update the list with the symbol
      const listRef = doc(db, COLL_LISTS, listId)
      const listDoc = await getDoc(listRef)

      if (!listDoc.exists()) {
        // Create the list if it doesn't exist
        await setDoc(listRef, {
          name: listId,
          items: [],
          createdOn: new Date(),
          modifiedOn: new Date()
        });
      }

      const currentItems = listDoc.data()?.items || []

      if (currentItems.includes(symbol)) {
        throw new Error(`${quoteData.symbol} is already in this watchlist`)
      }

      await updateDoc(listRef, {
        items: arrayUnion(symbol),
        symbols: arrayUnion(symbol),
        modifiedOn: now,
      })

      return symbol
    } catch (error) {
      console.error('Error adding item to watchlist:', error)
      throw error
    }
  }

  const getListInfo = async (listId: string): Promise<ListInfo | null> => {
    try {
      const listRef = doc(db, COLL_LISTS, listId)
      const listDoc = await getDoc(listRef)
      return listDoc.exists()
        ? { id: listDoc.id, ...listDoc.data() } as ListInfo
        : null
    } catch (error) {
      console.error('Error fetching list item:', error)
      throw error
    }
  }

  /**
   * Get an item by symbol from the items collection
   * Used for database-first symbol lookup before API validation
   * @param symbol The symbol to look up (will be converted to uppercase)
   * @returns Promise<ItemInfo | null> The item data or null if not found
   */
  const getItemBySymbol = async (symbol: string): Promise<ItemInfo | null> => {
    try {
      if (!db) throw new Error('Firebase is not configured');
      if (!symbol) throw new Error('Symbol is required');

      const upperSymbol = symbol.toUpperCase();
      const itemRef = doc(db, COLL_ITEMS, upperSymbol);
      const itemDoc = await getDoc(itemRef);

      if (!itemDoc.exists()) {
        return null;
      }

      return {
        id: itemDoc.id,
        ...itemDoc.data()
      } as ItemInfo;
    } catch (error) {
      console.error('Error fetching item by symbol:', error);
      return null;
    }
  }

  const getVisitors = async () => {
    try {
      const visitorsRef = doc(db, COLL_VISITORS, "2024-11-25");
      const visitorsDoc = await getDoc(visitorsRef)
      return visitorsDoc.exists() ? visitorsDoc.data() : {}
    } catch (error) {
      console.error('Error fetching visitors:', error)
      throw error
    }
  }

  const subscribeToNewsletter = async (email: string) => {
    try {
      const subscribersRef = collection(db, 'subscribers');
      await addDoc(subscribersRef, {
        email,
        subscribedAt: serverTimestamp(),
        status: 'active'
      });
      return true;
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }

  async function saveUserRemote(userInfo: UserInfo2): Promise<any> {
    try {
      if (!userInfo?.uid) {
        throw new Error('User ID is required');
      }

      const docRef = doc(db, `${EnumCollections.COL_USERS}/${userInfo.uid}`);
      const now = DateTimeHelper.getNow();

      // First check if the document exists
      const docSnap = await getDoc(docRef).catch(error => {
        console.error('Error fetching user document:', error);
        throw new Error('Failed to fetch user document');
      });

      let updatedUserInfo: UserInfo2;

      if (docSnap.exists()) {
        // Update existing document
        updatedUserInfo = {
          ...userInfo,
          modifiedOn: now
        };
      } else {
        // Create new document
        updatedUserInfo = {
          ...userInfo,
          createdOn: now,
          modifiedOn: now
        };
      }

      await setDoc(docRef, updatedUserInfo, { merge: true }).catch(error => {
        console.error('Error saving user document:', error);
        throw new Error('Failed to save user document');
      });

      return { ...updatedUserInfo, id: userInfo.uid };
    } catch (error) {
      console.error('Error in saveUserRemote:', error);
      throw error instanceof Error ? error : new Error('An unexpected error occurred while saving user data');
    }
  }

  const getHttpsCallable = (name: string) => {
    return httpsCallable(functions, name);
  };

  /**
   * Clear all market news from the marketnews collection
   * @returns Promise with the result of the operation
   */
  const clearAllMarketNews = async () => {
    try {
      const clearMarketNewsCallable = getHttpsCallable('callableClearAllMarketNews');
      const result = await clearMarketNewsCallable();
      return result.data;
    } catch (error) {
      console.error('Error clearing market news:', error);
      throw error;
    }
  };


  /**
   * Generates a text-based tree representation of the Firestore database structure
   * @returns Promise with the text representation
   */
  const generateFirestoreGraphText = async (): Promise<string> => {
    try {
      // Define the expected response type
      const callable = httpsCallable<any, { success: boolean, data: string }>(functions, 'generateFirestoreGraphTextCallable');

      // Add timeout to prevent hanging indefinitely
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Cloud Function call timed out after 30 seconds')), 30000);
      });

      // Race between the actual call and the timeout
      const result = await Promise.race([
        callable({}),
        timeoutPromise
      ]) as any;

      if (!result || !result.data) {
        throw new Error('No data received from Cloud Function');
      }

      // Check if result.data is already a string (direct response)
      if (typeof result.data === 'string') {
        return result.data;
      }

      // // If result.data is an object with a success property, it's the response from the Cloud Function
      // if (typeof result.data === 'object' && result.data.success) {
      //   // If result.data has a data property that's a string, use that
      //   if (typeof result.data.data === 'string') {
      //     return result.data.data;
      //   }

      //   // Otherwise, assume the graph text is in another property or needs to be extracted
      //   return result.data.toString();
      // }

      // // If result.data is an object, convert it to a string
      // if (typeof result.data === 'object') {
      //   // Check if it has a toString method that produces the graph text
      //   if (typeof result.data.toString === 'function' && 
      //       result.data.toString() !== '[object Object]' && 
      //       result.data.toString().includes('Firestore Database')) {
      //     return result.data.toString();
      // //   }

      //   // Otherwise, stringify it
      //   return JSON.stringify(result.data, null, 2);
      // }

      // If all else fails, convert result.data to a string
      return String(result.data);
    } catch (error: any) {
      // If the error message contains the graph text, return it
      if (error.message && error.message.includes('Firestore Database')) {
        return error.message;
      }

      throw error;
    }
  };

  /**
 * Used to get the client secret for the payment session
 * @param priceId
 */
  const createCheckoutSession = async (priceId: string, stripeCustomerId: string, returnUrl: string): Promise<string | null> => {
    const callable = httpsCallable<
      { priceId: string, stripeCustomerId: string, returnUrl: string },
      { clientSecret: string | null }
    >(functions, 'createCheckoutSession');

    // console.log("Going to call checkoutSession with:", { priceId, stripeCustomerId, returnUrl });
    const result = await callable({ priceId, stripeCustomerId, returnUrl });
    return result.data.clientSecret;
  };

  /**
   * Confirms a user in both Firebase and Stripe
   * @param userInfo The user info to confirm
   * @returns Updated UserInfo2 with Stripe customer ID or null if failed
   */
  const confirmCustomer = async (userInfo: UserInfo2): Promise<UserInfo2 | null> => {
    try {
      const callable = httpsCallable<
        { data: UserInfo2 },
        { result: boolean; userInfo: UserInfo2 | null }
      >(functions, 'confirmCustomer');

      // console.log('==========================================');
      // console.log('Calling confirmCustomer with:', userInfo);
      // console.log('==========================================');

      const result = await callable(JSON.parse(JSON.stringify(userInfo)));
      // console.log('Confirm customer result:', result.data);

      if (result.data.result && result.data.userInfo) {
        return result.data.userInfo;
      }
      return null;
    } catch (error) {
      console.error('Error confirming customer:', error);
      return null;
    }
  };

  function getPaymentInfosSnapshot(uid: string, callback: any): Unsubscribe {
    const collPath = `${EnumCollections.COL_USERS}/${uid}/${EnumCollections.COL_PAYMENTS}`;
    return getSnapshot(collPath, callback);
  }

  function getSubscriptionsSnapshot(uid: string, callback: any): Unsubscribe {
    const collPath = `${EnumCollections.COL_USERS}/${uid}/${EnumCollections.COL_SUBSCRIPTIONS}`;
    // Subscription path configured
    return getSnapshot(collPath, callback);
  }

  // function getSubscriptionsForUser(uid:string): Promise<SubscriptionInfo[]> {
  //   return new Promise((resolve) => {
  //     getSubscriptionsSnapshot(uid, (subscriptions: any) => {
  //       console.log("Subscriptions:", subscriptions);
  //       const allSubs = querySnapshotToDataWithId(subscriptions);
  //       console.log("All subscriptions:", allSubs);
  //       resolve(allSubs);
  //     });
  //   });
  // }

  // async function hasActiveSubscription(uid: string): Promise<boolean> {
  //   const subscriptions = await getSubscriptionsForUser(uid);
  //   return subscriptions.some(sub => 
  //     sub.status === 'active' && 
  //     !sub.cancelAtPeriodEnd && 
  //     sub.currentPeriodEnd.toDate() > new Date()
  //   );
  // }

  const getListInfosForUser = async (userId: string): Promise<ListInfo[]> => {
    try {
      const userDoc = await getDoc(doc(db, COLL_USERS, userId));
      const userData = userDoc.data();

      if (!userData || !userData.listIds || userData.listIds.length === 0) {
        return [];
      }

      const listInfos: ListInfo[] = [];

      for (const listId of userData.listIds) {
        const listDoc = await getDoc(doc(db, COLL_LISTS, listId));
        if (listDoc.exists()) {
          listInfos.push({ id: listDoc.id, ...listDoc.data() } as ListInfo);
        }
      }
      return listInfos;
    } catch (error) {
      console.error('Error in getListInfosForUser:', error);
      return [];
    }
  };

  /**
   * Get all users from the database
   * @returns Promise with array of UserInfo2 objects
   */
  async function getAllUsers(): Promise<UserInfo2[]> {
    const usersRef = collection(db, COLL_USERS);
    const querySnapshot = await getDocs(usersRef);
    const users: UserInfo2[] = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data() as UserInfo2;
      users.push(userData);
    });

    return users;
  }

  /**
   * Subscribe to the users collection
   * @param callback Function to call when users collection changes
   * @returns Unsubscribe function
   */
  function subscribeToUsers(callback: (users: UserInfo2[]) => void): Unsubscribe {
    const usersRef = collection(db, COLL_USERS);

    return onSnapshot(usersRef, (querySnapshot) => {
      const users: UserInfo2[] = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data() as UserInfo2;
        // Make sure to include the document ID as the uid
        userData.uid = doc.id;
        users.push(userData);
      });

      callback(users);
    });
  }

  /**
   * Delete a user completely from the system
   * This will:
   * 1. Delete the user from Firebase Auth (if it exists)
   * 2. Clean up all associated user data from Firestore
   * 
   * @param userId The ID of the user to delete
   * @returns Promise that resolves with the deletion result
   */
  async function deleteUser(userId: string): Promise<any> {
    try {
      // Ensure the user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to delete a user');
      }

      // Get the current user's token to ensure it's fresh
      await currentUser.getIdToken(true);
      const deleteUserCallable = httpsCallable(functions, 'deleteUser');
      const result = await deleteUserCallable({ userId });

      return result.data;
    } catch (error) {
      console.error(`Error deleting user ${userId}:`, error);
      throw error;
    }
  }



  /**
   * Trigger manual newsletter send for a specific user
   * @param userId The ID of the user to send the newsletter to
   * @returns Promise that resolves when the newsletter send is triggered
   */
  async function triggerManualNewsletterSend(userId: string): Promise<void> {
    try {
      // Ensure the user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to send a newsletter');
      }

      // Get the current user's token to ensure it's fresh
      await currentUser.getIdToken(true);
      const sendNewsletterCallable = httpsCallable(functions, 'sendNewsletterToUser');
      const result = await sendNewsletterCallable({ userId });
      return result.data as any;
    } catch (error) {
      console.error(`Error triggering newsletter send for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Verify a user's email (admin action)
   * @param userId The ID of the user to verify
   * @returns Promise that resolves with the verification result
   */
  async function verifyUserEmail(userId: string): Promise<{ success: boolean }> {
    try {
      // Ensure the user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to verify user email');
      }

      // Get the current user's token to ensure it's fresh
      await currentUser.getIdToken(true);
      const verifyEmailCallable = httpsCallable(functions, 'verifyEmail');
      const result = await verifyEmailCallable({ userId });
      return result.data as { success: boolean };
    } catch (error) {
      console.error(`Error verifying email for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Refresh email content and PDF for a specific user
   * @param userId The ID of the user to refresh content for
   * @returns Promise that resolves when the content refresh is complete
   */
  async function refreshUserEmailContent(userId: string): Promise<void> {
    try {
      // Ensure the user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to refresh user email content');
      }

      // Get the current user's token to ensure it's fresh
      await currentUser.getIdToken(true);
      console.log(`Attempting to refresh email content for user ${userId} as admin ${currentUser.uid}`);

      // Call the refreshUserEmailContent Cloud Function
      const refreshUserEmailContentFn = httpsCallable(functions, 'refreshUserEmailContent');
      const result = await refreshUserEmailContentFn({ userId });

      // Update the emailBodyUpdatedOnL timestamp in the user document
      const userRef = doc(db, COLL_USERS, userId);
      await updateDoc(userRef, {
        emailBodyUpdatedOnL: Date.now() // Store current time as milliseconds
      });

      console.log(`Email content refresh result for user ${userId}:`, result.data);
      return result.data as any;
    } catch (error) {
      console.error(`Error refreshing email content for user ${userId}:`, error);
      throw error;
    }
  }



  /**
   * Get all batch runs from the database
   * @returns Promise that resolves with an array of batch run information
   */
  async function getAllBatchRuns(): Promise<any[]> {
    try {
      const batchRunsRef = collection(db, COLL_BATCH_RUNS);
      const querySnapshot = await getDocs(query(batchRunsRef));

      const batchRuns: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        batchRuns.push({
          id: doc.id,
          ...data,
          createdOn: data.createdOn?.toDate() || new Date(),
          runStart: data.runStart?.toDate() || null,
          runEnd: data.runEnd?.toDate() || null,
        });
      });

      // Sort by creation date (newest first)
      return batchRuns.sort((a, b) => compareDates(a, b, 'createdOn'));
    } catch (error) {
      console.error('Error fetching batch runs:', error);
      return [];
    }
  }

  /**
   * Subscribe to batch runs collection for real-time updates
   * @param callback Function to call with updated batch runs data
   * @returns Unsubscribe function to stop listening for updates
   */
  function subscribeToBatchRuns(callback: (batchRuns: any[]) => void): Unsubscribe {
    const batchRunsRef = collection(db, COLL_BATCH_RUNS);
    return onSnapshot(batchRunsRef, (querySnapshot) => {
      const batchRuns: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        batchRuns.push({
          id: doc.id,
          ...data,
          createdOn: data.createdOn?.toDate() || new Date(),
        });
      });

      // Sort by creation date (newest first)
      callback(batchRuns.sort((a, b) => compareDates(a, b, 'createdOn')));
    }, (error) => {
      console.error('Error subscribing to batch runs:', error);
      callback([]);
    });
  }

  /**
   * Get list runs for a specific batch
   * @param batchId The ID of the batch to get list runs for
   * @returns Promise that resolves with an array of list run information
   */
  async function getListRunsForBatch(batchId: string): Promise<any[]> {
    try {
      // Query from nested collection: batchruns/{batchId}/listruns
      const listRunsRef = collection(db, COLL_BATCH_RUNS, batchId, COLL_LIST_RUNS);
      const querySnapshot = await getDocs(listRunsRef);

      const listRuns: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        listRuns.push({
          id: doc.id,
          ...data,
          createdOn: data.createdOn?.toDate() || new Date(),
          runStart: data.runStart?.toDate() || null,
          runEnd: data.runEnd?.toDate() || null,
        });
      });

      // Sort by creation date
      return listRuns.sort((a, b) => compareDates(a, b, 'createdOn'));
    } catch (error) {
      console.error(`Error fetching list runs for batch ${batchId}:`, error);
      return [];
    }
  }

  // Get item runs for a specific list run
  async function getItemRunsForListRun(batchId: string, listRunId: string): Promise<ItemRunInfo[]> {
    try {
      // Query from nested collection: batchruns/{batchId}/listruns/{listRunId}/items
      const itemsRef = collection(db, COLL_BATCH_RUNS, batchId, COLL_LIST_RUNS, listRunId, COLL_ITEMS);
      const querySnapshot = await getDocs(itemsRef);

      const itemRuns: ItemRunInfo[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Extract id separately and create the object with the correct interface properties
        const id = doc.id;
        const itemRun: ItemRunInfo = {
          itemId: data.itemId || id, // Use doc.id as fallback for itemId
          symbol: data.symbol || '',
          listRunId: data.listRunId || listRunId,
          batchId: data.batchId || '',
          isReadyToProcess: data.isReadyToProcess || false,
          isPending: data.isPending || false,
          isProcessing: data.isProcessing || false,
          isDone: data.isDone || false,
          isError: data.isError || false,
          createdOn: data.createdOn?.toDate() || new Date(),
          error: data.error || undefined,
          targetBatchId: data.targetBatchId
        };
        itemRuns.push(itemRun);
      });

      // Sort by symbol for easier reading
      return itemRuns.sort((a, b) => compareDates(a, b, 'createdOn'));
    } catch (error) {
      console.error(`Error fetching item runs for list run ${listRunId}:`, error);
      return [];
    }
  }

  // Get news data for a specific symbol
  async function getNewsForSymbol(symbol: string): Promise<NewsInfo[]> {
    try {
      // Directly access the document at root/news/[symbol]
      const newsDocRef = doc(db, COLL_NEWS, symbol);
      const newsDocSnap = await getDoc(newsDocRef);

      const newsItems: NewsInfo[] = [];

      if (newsDocSnap.exists()) {
        const data = newsDocSnap.data();
        const newsItem = new NewsInfo(newsDocSnap.id, data.itemId || '');
        newsItem.symbol = data.symbol || symbol; // Use the symbol parameter as fallback
        newsItem.long = data.long || '';
        newsItem.short = data.short || '';
        newsItem.articles = data.articles || [];
        newsItem.isArticlesUpdated = data.isArticlesUpdated || false;
        newsItem.createdOn = data.createdOn || new Date();
        newsItem.modifiedOn = data.modifiedOn || new Date();
        newsItem.refreshedOnL = data.refreshedOnL || 0;

        newsItems.push(newsItem);
      } else {
        // No news document found
      }

      return newsItems;
    } catch (error) {
      console.error(`Error fetching news for symbol ${symbol}:`, error);
      return [];
    }
  }

  // Subscribe to item runs for a specific list run
  function subscribeToItemRunsForListRun(batchId: string, listRunId: string, callback: (data: ItemRunInfo[]) => void): () => void {
    // Subscribing to item runs for list

    // Query from nested collection: batchruns/{batchId}/listruns/{listRunId}/items
    const itemsRef = collection(db, COLL_BATCH_RUNS, batchId, COLL_LIST_RUNS, listRunId, COLL_ITEMS);

    const unsubscribe = onSnapshot(itemsRef, (querySnapshot) => {
      // Snapshot received

      const itemRuns: ItemRunInfo[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Extract id separately and create the object with the correct interface properties
        const id = doc.id;
        const itemRun: ItemRunInfo = {
          itemId: data.itemId || id, // Use doc.id as fallback for itemId
          symbol: data.symbol || '',
          listRunId: data.listRunId || listRunId,
          batchId: data.batchId || '',
          isReadyToProcess: data.isReadyToProcess || false,
          isPending: data.isPending || false,
          isProcessing: data.isProcessing || false,
          isDone: data.isDone || false,
          isError: data.isError || false,
          createdOn: data.createdOn?.toDate() || new Date(),
          updatedOn: data.updatedOn?.toDate() || null,
          error: data.error || undefined,
          targetBatchId: data.targetBatchId
        };
        itemRuns.push(itemRun);
      });

      // Returning items
      // Sort by symbol for easier reading
      callback(itemRuns.sort((a, b) => a.symbol.localeCompare(b.symbol)));
    }, (error) => {
      console.error(`❌ [subscribeToItemRunsForListRun] Error subscribing to item runs for list run ${listRunId}:`, error);
      callback([]);
    });

    return unsubscribe;
  }

  /**
   * Subscribe to list runs for a specific batch for real-time updates
   * @param batchId The ID of the batch to subscribe to list runs for
   * @param callback Function to call with updated list runs data
   * @returns Unsubscribe function to stop listening for updates
   */
  function subscribeToListRunsForBatch(batchId: string, callback: (listRuns: any[]) => void): Unsubscribe {
    // Subscribing to list runs for batch

    // Query from nested collection: batchruns/{batchId}/listruns
    const listRunsRef = collection(db, COLL_BATCH_RUNS, batchId, COLL_LIST_RUNS);

    // Store unsubscribe functions for item subscriptions
    const itemUnsubscribes: { [listRunId: string]: Unsubscribe } = {};

    // Main subscription to list runs
    const listRunsUnsubscribe = onSnapshot(listRunsRef, (querySnapshot) => {
      // Snapshot received

      const listRuns: any[] = [];

      // Clean up any previous item subscriptions for list runs that no longer exist
      const currentListRunIds = new Set<string>();

      querySnapshot.forEach((doc) => {
        const listRunId = doc.id;
        currentListRunIds.add(listRunId);

        const data = doc.data();

        listRuns.push({
          id: listRunId,
          ...data,
          createdOn: data.createdOn?.toDate() || new Date(),
          runStart: data.runStart?.toDate() || null,
          runEnd: data.runEnd?.toDate() || null,
        });

        // Subscribe to items for this list run if not already subscribed
        if (!itemUnsubscribes[listRunId]) {
          // Use nested path: batchruns/{batchId}/listruns/{listRunId}/items
          const itemsRef = collection(db, COLL_BATCH_RUNS, batchId, COLL_LIST_RUNS, listRunId, COLL_ITEMS);
          itemUnsubscribes[listRunId] = onSnapshot(itemsRef, (itemsSnapshot) => {
            // Check if any items are in processing state
            let anyItemProcessing = false;
            itemsSnapshot.forEach((itemDoc) => {
              const itemData = itemDoc.data();
              if (itemData.isProcessing) {
                anyItemProcessing = true;
              }
            });

            // If any item is processing, update the list run status in the UI
            if (anyItemProcessing) {
              const updatedListRuns = listRuns.map(run => {
                if (run.id === listRunId) {
                  return { ...run, isProcessing: true };
                }
                return run;
              });

              // Sort by creation date
              callback(updatedListRuns.sort((a, b) => compareDates(a, b, 'createdOn', true)));
            }
          });
        }
      });

      // Remove subscriptions for list runs that no longer exist
      Object.keys(itemUnsubscribes).forEach(id => {
        if (!currentListRunIds.has(id)) {
          itemUnsubscribes[id]();
          delete itemUnsubscribes[id];
        }
      });

      // Returning list runs
      // Sort by creation date
      callback(listRuns.sort((a, b) => compareDates(a, b, 'createdOn', true)));
    }, (error) => {
      console.error(`❌ [subscribeToListRunsForBatch] Error subscribing to list runs for batch ${batchId}:`, error);
      callback([]);
    });

    // Return a function that unsubscribes from both list runs and all item subscriptions
    return () => {
      listRunsUnsubscribe();
      Object.values(itemUnsubscribes).forEach(unsubscribe => unsubscribe());
    };
  }

  /**
   * Subscribe to logs for a specific batch run
   * @param batchId The batch ID to get logs for
   * @param callback Function to call with updated logs data
   * @returns Unsubscribe function
   */
  function subscribeToBatchLogs(batchId: string, callback: (logs: any[]) => void): Unsubscribe {
    const logsRef = collection(db, 'batchruns', batchId, 'logs');
    const q = query(logsRef, orderBy('timestamp', 'asc'));

    return onSnapshot(q, (querySnapshot) => {
      const logs: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          message: data.message || '',
          timestamp: data.timestamp?.toDate() || new Date(),
          level: data.level || 'INFO'
        });
      });

      // Sort by timestamp
      callback(logs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()));
    }, (error) => {
      console.error(`Error subscribing to logs for batch ${batchId}:`, error);
      callback([]);
    });
  }

  /**
   * Enqueue a batch run of the specified type
   * @param batchType The numeric type of batch to enqueue (use EnumBatchType enum)
   * @param parentBatchId Optional parent batch ID (required for USER_PREP type)
   * @param description Optional description of how/why the batch was created
   * @param targetTimeSlot Optional target time slot (0=MORNING, 1=MIDDAY, 2=EVENING)
   * @returns Promise that resolves with the ID of the created batch run
   */
  async function enqueueBatch(batchType: number, parentBatchId?: string, description?: string, targetTimeSlot?: number): Promise<string | null> {
    try {
      const enqueueBatchFn = httpsCallable(functions, 'enqueueBatchCallable', { timeout: BATCH_TIMEOUT * 1000 });

      // Build params object, only including defined values
      const params: any = { batchType };
      if (parentBatchId !== undefined) params.parentBatchId = parentBatchId;
      if (description !== undefined) params.description = description;
      if (targetTimeSlot !== undefined) params.targetTimeSlot = targetTimeSlot;

      const result = await enqueueBatchFn(params);
      return result.data as string;
    } catch (error) {
      console.error(`Error enqueueing batch type ${batchType}:`, error);
      return null;
    }
  }


  /**
   * Enqueue reserved lists (TOP_LEADERS and TOP_LOSERS) for processing
   * @param targetTimeSlot Optional target time slot (0=MORNING, 1=MIDDAY, 2=EVENING)
   * @returns Promise that resolves with the ID of the created batch run
   */
  async function enqueueReservedLists(targetTimeSlot?: number): Promise<string | null> {
    return enqueueBatch(Number(EnumBatchType.PREPARE_RESERVED_LIST), undefined, 'UI:enqueueBatch:ReservedLists', targetTimeSlot);
  }

  /**
   * Legacy function for backward compatibility
   * @deprecated Use enqueueReservedLists instead
   */
  async function rebuildReservedLists(): Promise<string | null> {
    return enqueueReservedLists();
  }

  /**
   * Enqueue all users with lists for batch processing
   * This function calls the backend to get all users with lists and enqueue them
   * @param targetTimeSlot Optional target time slot (0=MORNING, 1=MIDDAY, 2=EVENING)
   * @returns Promise that resolves with the batch ID and user count
   */
  async function enqueueAllUserLists(targetTimeSlot?: number): Promise<{ batchId: string; userCount: number; userIds: string[] } | null> {
    try {
      const enqueueAllUserListsFn = httpsCallable<{ targetTimeSlot?: number }, { batchId: string; userCount: number; userIds: string[] }>(
        functions,
        'enqueueAllUsersCallable'
      );

      // Build params object, only including targetTimeSlot if defined
      const params: any = {};
      if (targetTimeSlot !== undefined) params.targetTimeSlot = targetTimeSlot;

      const result = await enqueueAllUserListsFn(params);
      return result.data;
    } catch (error) {
      console.error('Error enqueueing all user lists:', error);
      throw error;
    }
  }

  // /**
  //  * Trigger a user preparation batch with a parent batch ID
  //  * @param parentBatchId The ID of the parent batch
  //  * @returns Promise that resolves with the ID of the created batch run
  //  */
  // async function triggerUserPrepBatch(parentBatchId: string): Promise<string | null> {
  //   return enqueueBatch(Number(EnumBatchType.USER_AND_BATCH_PREP), parentBatchId);
  // }

  /**
   * Confirm reserved lists exist and are up to date
   * This will create or update TOP_LEADERS, TOP_LOSERS, and TOP_SP500 lists
   * @returns Promise that resolves with the result of the confirm operation
   */
  const confirmReservedLists = async () => {
    try {
      const confirmReservedListsFn = httpsCallable(functions, 'callableConfirmReservedList');
      const result = await confirmReservedListsFn();
      console.log('Confirm reserved lists result:', result);
      return result;
    } catch (error) {
      console.error('Error confirming reserved lists:', error);
      throw error;
    }
  };

  /**
   * Update the isActive status of a list
   * @param listId The ID of the list to update
   * @param isActive The new isActive status (boolean)
   * @returns Promise that resolves with the result of the update operation
   */
  const updateListActiveStatus = async (listId: string, isActive: boolean) => {
    try {
      const updateListActiveStatusFn = httpsCallable<{ listId: string; isActive: boolean }, { success: boolean; listId: string; isActive: boolean; message: string }>(
        functions,
        'callableUpdateListActiveStatus'
      );
      const result = await updateListActiveStatusFn({ listId, isActive });
      console.log('Update list active status result:', result);
      return result.data;
    } catch (error) {
      console.error('Error updating list active status:', error);
      throw error;
    }
  };

  /**
   * Subscribe to system run settings document in Firestore
   * @param callback Function to call when settings change
   * @returns Unsubscribe function
   */
  function subscribeToSystemRunSettings(callback: (settings: any) => void): Unsubscribe {
    try {
      // Check if the user is authenticated
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to subscribe to system settings');
      }

      const settingsRef = doc(db, 'settings', 'systemRun');

      // Subscribe to the document
      return onSnapshot(settingsRef, (snapshot) => {
        if (!snapshot.exists()) {
          // Return default settings if document doesn't exist
          callback({
            autoRun: false,
            autoRunPeriod: 5,
            isBatchListRunInProgress: false,
            runStdListRefresh: false,
            runUserRefresh: false,
            isTriggered: true, // Default to true for backward compatibility
            isAllowAutoScheduledRuns: true // Default to true for backward compatibility
          });
          return;
        }

        const data = snapshot.data();

        // Convert timestamp to Date if it exists
        if (data.timestamp && typeof data.timestamp.toDate === 'function') {
          data.timestamp = data.timestamp.toDate();
        }
        if (data.lastBatchRunStart && typeof data.lastBatchRunStart.toDate === 'function') {
          data.lastBatchRunStart = data.lastBatchRunStart.toDate();
        }

        callback(data);
      }, (error) => {
        console.error('Error subscribing to system run settings:', error);
      });
    } catch (error) {
      console.error('Error setting up subscription to system run settings:', error);
      throw error;
    }
  }

  /**
   * Get system run settings from Firestore
   * @returns Promise that resolves with the system run settings
   */
  async function getSystemRunSettings(): Promise<any> {
    try {
      // Check if the user is authenticated
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to get system settings');
      }

      // Check if the user is an admin by querying their user document
      const userRef = doc(db, COLL_USERS, auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data() as UserInfo2;

      if (!userData?.isAdmin) {
        throw new Error('Admin privileges required to get system settings');
      }

      const settingsRef = doc(db, 'settings', 'systemRun');
      const settingsSnap = await getDoc(settingsRef);

      if (!settingsSnap.exists()) {
        // Return default settings if document doesn't exist
        return {
          autoRun: false,
          autoRunPeriod: 5,
          isBatchListRunInProgress: false,
          runStdListRefresh: false,
          runUserRefresh: false,
          isTriggered: true, // Default to true for backward compatibility
          isAllowAutoScheduledRuns: true // Default to true for backward compatibility
        };
      }

      const data = settingsSnap.data();

      // Convert timestamp to Date if it exists
      if (data.lastBatchRunStart && typeof data.lastBatchRunStart.toDate === 'function') {
        data.lastBatchRunStart = data.lastBatchRunStart.toDate();
      }

      return data;
    } catch (error) {
      console.error('Error getting system run settings:', error);

      // Return default settings on error
      return {
        autoRun: false,
        autoRunPeriod: 5,
        isBatchListRunInProgress: false,
        runStdListRefresh: false,
        runUserRefresh: false,
        isTriggered: true, // Default to true for backward compatibility
        isAllowAutoScheduledRuns: true // Default to true for backward compatibility
      };
    }
  }

  /**
   * Update system settings in Firestore
   * @param settings Object containing system settings to update
   * @returns Promise that resolves when the update is complete
   */
  async function updateSystemSettings(settings: Record<string, any>): Promise<void> {
    try {
      // Check if the user is authenticated
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to update system settings');
      }

      // Check if the user is an admin by querying their user document
      const userRef = doc(db, COLL_USERS, auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data() as UserInfo2;

      if (!userData?.isAdmin) {
        throw new Error('Admin privileges required to update system settings');
      }

      const settingsRef = doc(db, 'settings', 'systemRun');

      // Add timestamp to track when settings were last updated
      const updatedSettings = {
        ...settings,
        timestamp: serverTimestamp()
      };

      await setDoc(settingsRef, updatedSettings, { merge: true });
      console.log('System settings updated successfully:', settings);
    } catch (error) {
      console.error('Error updating system settings:', error);
      throw error;
    }
  }

  // Default values for thresholds (matching constants in functions/src/constants.ts)
  const DEFAULT_NEWS_CACHE_HOURS = 48;
  const DEFAULT_SENTIMENT_CACHE_HOURS = 48;

  /**
   * Subscribe to thresholds settings document in Firestore
   * @param callback Function to call when settings change
   * @returns Unsubscribe function
   */
  function subscribeToThresholds(callback: (settings: any) => void): Unsubscribe {
    try {
      // Check if the user is authenticated
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to subscribe to thresholds settings');
      }

      const settingsRef = doc(db, 'settings', 'thresholds');

      // Subscribe to the document
      return onSnapshot(settingsRef, (snapshot) => {
        if (!snapshot.exists()) {
          // Return default settings if document doesn't exist
          callback({
            newsCacheDurationHours: DEFAULT_NEWS_CACHE_HOURS,
            sentimentCacheDurationHours: DEFAULT_SENTIMENT_CACHE_HOURS
          });
          return;
        }

        const data = snapshot.data();

        // Convert timestamp to Date if it exists
        if (data.timestamp && typeof data.timestamp.toDate === 'function') {
          data.timestamp = data.timestamp.toDate().getTime();
        }

        callback({
          newsCacheDurationHours: data.newsCacheDurationHours ?? DEFAULT_NEWS_CACHE_HOURS,
          sentimentCacheDurationHours: data.sentimentCacheDurationHours ?? DEFAULT_SENTIMENT_CACHE_HOURS,
          timestamp: data.timestamp
        });
      }, (error) => {
        console.error('Error subscribing to thresholds settings:', error);
      });
    } catch (error) {
      console.error('Error setting up subscription to thresholds settings:', error);
      throw error;
    }
  }

  /**
   * Update thresholds settings in Firestore
   * @param settings Object containing thresholds settings to update
   * @returns Promise that resolves when the update is complete
   */
  async function updateThresholds(settings: Record<string, any>): Promise<void> {
    try {
      // Check if the user is authenticated
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to update thresholds settings');
      }

      // Check if the user is an admin by querying their user document
      const userRef = doc(db, COLL_USERS, auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data() as UserInfo2;

      if (!userData?.isAdmin) {
        throw new Error('Admin privileges required to update thresholds settings');
      }

      const settingsRef = doc(db, 'settings', 'thresholds');

      // Add timestamp to track when settings were last updated
      const updatedSettings = {
        ...settings,
        timestamp: serverTimestamp()
      };

      await setDoc(settingsRef, updatedSettings, { merge: true });
      console.log('Thresholds settings updated successfully:', settings);
    } catch (error) {
      console.error('Error updating thresholds settings:', error);
      throw error;
    }
  }

  /**
   * Process the next batch of list runs
   * @returns Promise that resolves with the result of the batch processing
   */
  async function processNextBatch(): Promise<any> {
    try {
      const processNextBatchFn = httpsCallable(functions, 'processNextBatchCallable', { timeout: BATCH_TIMEOUT * 1000 });
      const result = await processNextBatchFn({});
      return result.data;
    } catch (error) {
      console.error('Error processing next batch:', error);
      throw error;
    }
  }

  async function checkAndRunNextAvailableBatch(): Promise<any> {
    try {
      const checkAndRunNextAvailableBatchFn = httpsCallable(functions, 'checkAndRunNextAvailableBatchCallable', { timeout: BATCH_TIMEOUT * 1000 });
      const result = await checkAndRunNextAvailableBatchFn({});
      return result.data;
    } catch (error) {
      console.error('Error checking and running next available batch:', error);
      throw error;
    }
  }



  /**
   * Delete a batch run and all its associated data (child batches, list runs, subcollections)
   * This calls the server-side function for complete cleanup
   * @param batchId The ID of the primary batch run to delete
   * @returns Promise that resolves when the deletion is complete
   */
  async function deleteBatchRun(batchId: string): Promise<boolean> {
    try {
      const deleteBatchRunFn = httpsCallable(functions, 'deleteBatchRunByIdCallable');
      const result = await deleteBatchRunFn({ batchId });
      const data = result.data as { success: boolean; error?: string };

      if (data.success) {
        console.log(`Successfully deleted batch run ${batchId} and all related data`);
        return true;
      } else {
        console.error(`Failed to delete batch run ${batchId}: ${data.error}`);
        return false;
      }
    } catch (error) {
      console.error(`Error deleting batch run ${batchId}:`, error);
      return false;
    }
  }

  /**
   * Reset a batch to its default runnable state
   * @param batchId ID of the batch to reset
   * @returns Promise<boolean> whether the reset was successful
   */
  async function resetBatch(batchId: string): Promise<boolean> {
    try {
      const resetBatchFn = httpsCallable(functions, 'resetBatchCallable');
      const result = await resetBatchFn({ batchId });
      return (result.data as any).success;
    } catch (error) {
      console.error(`Error resetting batch ${batchId}:`, error);
      return false;
    }
  }

  /**
   * Process a single batch by ID - handles both parent and child batches
   * @param batchId ID of the batch to process
   * @param forceRun If true, reset the batch to pending state before processing
   * @returns Promise with the result of processing the batch
   */
  async function processSingleBatch(batchId: string, forceRun: boolean = false): Promise<any> {
    try {
      const processSingleBatchFn = httpsCallable(functions, 'processSingleBatchCallable', { timeout: BATCH_TIMEOUT * 1000 });
      const result = await processSingleBatchFn({ batchId, forceRun });
      return result.data;
    } catch (error) {
      console.error(`Error processing single batch ${batchId}:`, error);
      throw error;
    }
  }


  /**
   * Process a document using Google Document AI
   * @param fileData Base64 encoded file data
   * @param fileName Name of the file
   * @param mimeType MIME type of the file
   * @returns Promise with the processing result
   */
  async function processDocument(fileData: string, fileName: string, mimeType: string): Promise<any> {
    try {
      const processDocumentFn = httpsCallable(functions, 'processDocumentCallable');
      const result = await processDocumentFn({ fileData, fileName, mimeType });
      return result.data;
    } catch (error) {
      console.error('Error processing document:', error);
      throw error;
    }
  }

  /**
   * Extract stock tickers from an image using the AI image handler
   * @param base64Data Base64 encoded image data
   * @returns Promise with the extracted tickers
   */
  async function getTickersFromImage(base64Data: string): Promise<string[]> {
    try {
      const extractTickersFn = httpsCallable(functions, 'getTickersFromImageCallable');
      const result = await extractTickersFn({ imageBase64: base64Data });
      return (result.data as any).tickers || [];
    } catch (error) {
      console.error('Error extracting tickers from image:', error);
      throw error;
    }
  }

  /**
   * Admin-only: refresh news for a single symbol by invoking the
   * `refreshItemNewsCallable` Firebase callable. Returns the canonical
   * `{ success, data?, error? }` shape unwrapped from the callable result.
   * @param symbol Ticker symbol to refresh
   */
  async function refreshItemNews(symbol: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const refreshItemNewsFn = httpsCallable(functions, 'refreshItemNewsCallable');
      const result = await refreshItemNewsFn({ symbol });
      return result.data as { success: boolean; data?: any; error?: string };
    } catch (error) {
      console.error(`Error refreshing item news for ${symbol}:`, error);
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  }


  /**
   * Clear all logs for a given batch (delete all docs in batchruns/{batchId}/logs)
   * @param batchId Batch ID whose logs should be cleared
   * @returns Promise<boolean> indicating success
   */
  const clearBatchLogs = async (batchId: string): Promise<boolean> => {
    try {
      const logsRef = collection(db, COLL_BATCH_RUNS, batchId, 'logs');
      const logsSnap = await getDocs(logsRef);
      if (logsSnap.empty) return true;
      const batch = writeBatch(db);
      logsSnap.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
      await batch.commit();
      return true;
    } catch (err) {
      console.error(`Error clearing logs for batch ${batchId}:`, err);
      return false;
    }
  };

  /**
   * Subscribe to the latest market news from the marketnews collection
   * @param onData Callback function to handle the latest market news data
   * @param onError Callback function to handle any errors
   * @returns Unsubscribe function
   */
  const getMultipleMarketNewsSnapshot = (
    limitCount: number,
    onData: (data: any[]) => void,
    onError: (error: any) => void,
    userInfo?: any // Optional userInfo parameter for filtering by user's custom list
  ): Unsubscribe => {
    try {
      if (!db) throw new Error('Firebase is not configured');

      console.log(`Setting up marketnews subscription with collection: ${COLL_MARKET_NEWS}, limit: ${limitCount}, userInfo: ${userInfo ? 'provided' : 'not provided'}`);

      // Always fetch more items than requested to allow for backfill if needed
      const fetchLimit = limitCount + 4; // Get 4 extra items for potential backfill

      // Create a query for the marketnews collection, ordered by timestamp (descending)
      const q = query(
        collection(db, COLL_MARKET_NEWS),
        orderBy('createdOnL', 'desc'),
        limit(fetchLimit) // Fetch extra items to ensure we have enough after filtering
      );

      // Set up the snapshot listener
      console.log('Setting up onSnapshot listener for multiple items');
      return onSnapshot(
        q,
        (snapshot) => {
          console.log('Multiple marketnews snapshot received, empty:', snapshot.empty, 'size:', snapshot.size);

          if (snapshot.empty) {
            // No market news found
            console.log('No market news documents found in collection');
            onData([]);
            return;
          }

          // Process all documents - Add isPersonalized flag directly here
          let marketNewsItems = snapshot.docs.map(doc => {
            const docData = doc.data();
            const userId = docData.userId || null;

            // Item is personalized if it has a userId matching the current user
            const isPersonalized = !!(userId && userInfo && userInfo.uid && userId === userInfo.uid);

            return {
              id: doc.id,
              ...docData,
              // Ensure createdOnL is present (timestamp in milliseconds)
              createdOnL: docData.createdOnL || docData.createdOn?.toDate()?.getTime() || Date.now(),
              // Keep date objects for backwards compatibility
              createdOn: docData.createdOn?.toDate() || new Date(),
              summaryGeneratedAt: docData.createdOn?.toDate() || new Date(),
              // Add isPersonalized flag
              isPersonalized: isPersonalized
            };
          }).filter((item: any) => (item.shortSummary || '').trim().indexOf('Error') === -1);

          // Apply user-specific filtering if userInfo is provided
          if (userInfo && userInfo.uid) {
            console.log(`Filtering market news for user with ID: ${userInfo.uid}`);

            // Filter market news items that have a userId matching the current user
            const userSpecificItems = marketNewsItems.filter((item: any) => {
              return item.userId === userInfo.uid;
            });

            // Filter items that don't have a userId (general items)
            const generalItems = marketNewsItems.filter((item: any) => {
              return !item.userId;
            });

            console.log(`Found ${userSpecificItems.length} user-specific items and ${generalItems.length} general items`);

            if (userSpecificItems.length >= limitCount) {
              // If we have enough user-specific items, use just those (limited to requested count)
              console.log(`Using ${limitCount} user-specific items`);
              marketNewsItems = userSpecificItems.slice(0, limitCount);
            } else if (userSpecificItems.length > 0) {
              // If we have some user-specific items but not enough, backfill with general items
              const backfillCount = limitCount - userSpecificItems.length;
              console.log(`Using ${userSpecificItems.length} user-specific items and backfilling with ${backfillCount} general items`);

              // Combine user-specific items with general items for backfill
              marketNewsItems = [
                ...userSpecificItems,
                ...generalItems.slice(0, backfillCount)
              ];
            } else {
              // If no user-specific items found, use general items up to the limit
              console.log(`No user-specific items found, using ${Math.min(limitCount, generalItems.length)} general items`);
              marketNewsItems = generalItems.slice(0, limitCount);
            }
          } else {
            // For non-logged-in users, just use general items up to the limit
            const generalItems = marketNewsItems.filter((item: any) => !item.userId);
            console.log(`Not logged in, using ${Math.min(limitCount, generalItems.length)} general items`);
            marketNewsItems = generalItems.slice(0, limitCount);
          }

          console.log(`Processed ${marketNewsItems.length} market news items`);
          onData(marketNewsItems);
        },
        (error) => {
          console.error('Error in multiple marketnews snapshot listener:', error);
          onError(error);
        }
      );
    } catch (error) {
      console.error('Error in getMultipleMarketNewsSnapshot:', error);
      onError(error);
      return () => { }; // Return empty unsubscribe function on error
    }
  };

  /**
   * Subscribe to SEC filings from the marketnews collection
   * @param limitCount Maximum number of SEC filings to return
   * @param onData Callback function to handle the SEC filings data
   * @param onError Callback function to handle any errors
   * @returns Unsubscribe function
   */
  const getSecFilingsSnapshot = (
    limitCount: number,
    onData: (data: any[]) => void,
    onError: (error: any) => void
  ): Unsubscribe => {
    try {
      if (!db) throw new Error('Firebase is not configured');

      console.log(`Setting up SEC filings subscription with limit: ${limitCount}`);

      // Create a query for SEC filings only
      const q = query(
        collection(db, COLL_MARKET_NEWS),
        where('isSecFiling', '==', true),
        orderBy('createdOnL', 'desc'),
        limit(limitCount)
      );

      return onSnapshot(
        q,
        (snapshot) => {
          console.log('SEC filings snapshot received, empty:', snapshot.empty, 'size:', snapshot.size);

          if (snapshot.empty) {
            console.log('No SEC filings found');
            onData([]);
            return;
          }

          const secFilings = snapshot.docs.map(doc => {
            const docData = doc.data();
            return {
              id: doc.id,
              ...docData,
              summaryGeneratedAt: docData.createdOn?.toDate() || new Date(),
              isSecFiling: true
            };
          });

          console.log(`Processed ${secFilings.length} SEC filings`);
          onData(secFilings);
        },
        (error) => {
          console.error('Error in SEC filings snapshot listener:', error);
          onError(error);
        }
      );
    } catch (error) {
      console.error('Error in getSecFilingsSnapshot:', error);
      onError(error);
      return () => { };
    }
  };

  const getLatestMarketNewsSnapshot = (
    onData: (data: any) => void,
    onError: (error: any) => void
  ): Unsubscribe => {
    try {
      if (!db) throw new Error('Firebase is not configured');

      console.log('Setting up marketnews subscription with collection:', COLL_MARKET_NEWS);

      // Create a query for the marketnews collection, ordered by timestamp (descending)
      const q = query(
        collection(db, COLL_MARKET_NEWS),
        orderBy('createdOnL', 'desc'),
        limit(1) // Limit to 1 to get only the latest entry
      );

      // Log the query for debugging
      console.log('Marketnews query created');

      // Set up the snapshot listener
      console.log('Setting up onSnapshot listener');
      return onSnapshot(
        q,
        (snapshot) => {
          console.log('Marketnews snapshot received, empty:', snapshot.empty, 'size:', snapshot.size);

          if (snapshot.empty) {
            // No market news found
            console.log('No market news documents found in collection');
            onData(null);
            return;
          }

          // Get the first (latest) document
          const latestDoc = snapshot.docs[0];
          console.log('Latest marketnews document ID:', latestDoc.id);

          const docData = latestDoc.data();
          console.log('Document data:', docData);

          const marketNewsData = {
            id: latestDoc.id,
            ...docData,
            // Ensure date objects are properly converted
            summaryGeneratedAt: docData.createdOn?.toDate() || new Date()
          };

          console.log('Processed market news data:', marketNewsData);
          onData(marketNewsData);
        },
        (error) => {
          console.error('Error in marketnews snapshot listener:', error);
          onError(error);
        }
      );
    } catch (error) {
      console.error('Error in getLatestMarketNewsSnapshot:', error);
      onError(error);
      return () => { }; // Return empty unsubscribe function on error
    }
  };

  const getLatestMarketNewsSnapshotRealtime = (
    onData: (data: any) => void,
    onError: (error: Error) => void
  ): Unsubscribe => {
    try {
      console.log('Setting up market news snapshot listener');
      const marketNewsRef = collection(db, COLL_MARKET_NEWS);
      const q = query(
        marketNewsRef,
        orderBy('createdOn', 'desc'),
        limit(5)
      );

      return onSnapshot(
        q,
        (snapshot) => {
          console.log(`Received ${snapshot.docs.length} market news documents`);
          const data = querySnapshotToDataWithId(snapshot);
          onData(data);
        },
        (error) => {
          console.error('Error in marketnews snapshot listener:', error);
          onError(error);
        }
      );
    } catch (error) {
      console.error('Error in getLatestMarketNewsSnapshot:', error);
      onError(error instanceof Error ? error : new Error(String(error)));
      return () => { }; // Return empty unsubscribe function on error
    }
  };

  // Get all prompts with optional filtering for active only
  const getPrompts = async (activeOnly?: boolean): Promise<AIInstruction[]> => {
    try {
      console.log('Getting prompts, activeOnly:', activeOnly);
      const promptsRef = collection(db, COLL_PROMPTS);
      let q = query(promptsRef, orderBy('updatedOn', 'desc'));

      if (activeOnly === true) {
        q = query(promptsRef, where('isActive', '==', true), orderBy('updatedOn', 'desc'));
      }

      const snapshot = await getDocs(q);
      console.log(`Retrieved ${snapshot.docs.length} prompts`);

      return snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as AIInstruction[];
    } catch (error) {
      console.error('Error getting prompts:', error);
      return [];
    }
  };

  // Subscribe to prompts with real-time updates
  const subscribeToPrompts = (onData: (prompts: AIInstruction[]) => void, onError: (error: Error) => void, activeOnly?: boolean): Unsubscribe => {
    try {
      console.log('Setting up prompts snapshot listener, activeOnly:', activeOnly);
      const promptsRef = collection(db, COLL_PROMPTS);
      let q = query(promptsRef, orderBy('updatedOn', 'desc'));

      if (activeOnly === true) {
        q = query(promptsRef, where('isActive', '==', true), orderBy('updatedOn', 'desc'));
      }

      return onSnapshot(
        q,
        (snapshot) => {
          console.log(`Received ${snapshot.docs.length} prompts`);
          const prompts = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          })) as AIInstruction[];
          onData(prompts);
        },
        (error) => {
          console.error('Error in prompts snapshot listener:', error);
          onError(error);
        }
      );
    } catch (error) {
      console.error('Error in subscribeToPrompts:', error);
      onError(error instanceof Error ? error : new Error(String(error)));
      return () => { }; // Return empty unsubscribe function on error
    }
  };

  // Update prompt active status
  const updatePromptActiveStatus = async (promptId: string, isActive: boolean): Promise<boolean> => {
    try {
      console.log(`Updating prompt ${promptId} active status to ${isActive}`);
      const promptRef = doc(db, COLL_PROMPTS, promptId);
      await updateDoc(promptRef, {
        isActive,
        updatedOn: serverTimestamp()
      });
      console.log('Prompt active status updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating prompt active status:', error);
      return false;
    }
  };

  // Create or update a prompt
  const createOrUpdatePrompt = async (promptData: any, promptId?: string): Promise<boolean> => {
    try {
      if (promptId) {
        // Update existing prompt
        console.log(`Updating prompt ${promptId}`);
        const promptRef = doc(db, COLL_PROMPTS, promptId);
        await updateDoc(promptRef, {
          ...promptData,
          updatedOn: serverTimestamp()
        });
        console.log('Prompt updated successfully');
      } else {
        // Create new prompt
        console.log('Creating new prompt');
        const promptsRef = collection(db, COLL_PROMPTS);
        await addDoc(promptsRef, {
          ...promptData,
          createdOn: serverTimestamp(),
          updatedOn: serverTimestamp()
        });
        console.log('Prompt created successfully');
      }
      return true;
    } catch (error) {
      console.error('Error creating/updating prompt:', error);
      return false;
    }
  };

  // Delete a prompt
  const deletePrompt = async (promptId: string): Promise<boolean> => {
    try {
      console.log(`Deleting prompt ${promptId}`);
      const promptRef = doc(db, COLL_PROMPTS, promptId);
      await deleteDoc(promptRef);
      console.log('Prompt deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      return false;
    }
  };

  // Subscribe to news sources with real-time updates
  const subscribeToNewsSources = (onData: (sources: NewsSourceInfo[]) => void, onError?: (error: Error) => void): Unsubscribe => {
    try {
      console.log('Setting up news sources snapshot listener');
      const sourcesRef = collection(db, COLL_NEWS_SOURCES);
      const q = query(sourcesRef, orderBy('priority', 'desc'));

      return onSnapshot(
        q,
        (snapshot) => {
          console.log(`Received ${snapshot.docs.length} news sources`);
          const sources = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            // Helper function to convert Firestore Timestamp to Date
            const toDate = (timestamp: any): Date => {
              if (!timestamp) return new Date();
              if (timestamp instanceof Date) return timestamp;
              if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                return timestamp.toDate();
              }
              return new Date(timestamp);
            };

            return {
              id: docSnap.id,
              urlPart: data.urlPart || '',
              priority: data.priority || 0,
              isBlocked: data.isBlocked || false,
              isApproved: data.isApproved !== undefined ? data.isApproved : true,
              pendingCount: data.pendingCount || 0,
              blockCount: data.blockCount || 0,
              lastHydrationTimeSeconds: data.lastHydrationTimeSeconds,
              createdOn: toDate(data.createdOn),
              modifiedOn: toDate(data.modifiedOn)
            } as NewsSourceInfo;
          });
          onData(sources);
        },
        (error) => {
          console.error('Error in news sources snapshot listener:', error);
          if (onError) {
            onError(error instanceof Error ? error : new Error(String(error)));
          }
        }
      );
    } catch (error) {
      console.error('Error in subscribeToNewsSources:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
      return () => { }; // Return empty unsubscribe function on error
    }
  };

  // Create a new news source
  const createNewsSource = async (sourceData: Omit<NewsSourceInfo, 'id' | 'createdOn' | 'modifiedOn'>): Promise<boolean> => {
    try {
      console.log('Creating new news source');
      const sourcesRef = collection(db, COLL_NEWS_SOURCES);
      await addDoc(sourcesRef, {
        urlPart: sourceData.urlPart,
        priority: sourceData.priority || 0,
        isBlocked: sourceData.isBlocked || false,
        isApproved: sourceData.isApproved !== undefined ? sourceData.isApproved : true,
        createdOn: serverTimestamp(),
        modifiedOn: serverTimestamp()
      });
      console.log('News source created successfully');
      return true;
    } catch (error) {
      console.error('Error creating news source:', error);
      return false;
    }
  };

  // Update an existing news source
  const updateNewsSource = async (sourceId: string, updates: Partial<Omit<NewsSourceInfo, 'id' | 'createdOn'>>): Promise<boolean> => {
    try {
      console.log(`Updating news source ${sourceId}`);
      const sourceRef = doc(db, COLL_NEWS_SOURCES, sourceId);
      await updateDoc(sourceRef, {
        ...updates,
        modifiedOn: serverTimestamp()
      });
      console.log('News source updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating news source:', error);
      return false;
    }
  };

  // Delete a news source
  const deleteNewsSource = async (sourceId: string): Promise<boolean> => {
    try {
      console.log(`Deleting news source ${sourceId}`);
      const sourceRef = doc(db, COLL_NEWS_SOURCES, sourceId);
      await deleteDoc(sourceRef);
      console.log('News source deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting news source:', error);
      return false;
    }
  };

  // Subscribe to news tags with real-time updates
  const subscribeToNewsTags = (onData: (tags: NewsTagInfo[]) => void, onError?: (error: Error) => void, activeOnly?: boolean): Unsubscribe => {
    try {
      console.log('Setting up news tags snapshot listener, activeOnly:', activeOnly);
      const tagsRef = collection(db, COLL_NEWS_TAGS);
      let q = query(tagsRef, orderBy('priority', 'desc'));

      if (activeOnly === true) {
        q = query(tagsRef, where('isActive', '==', true), orderBy('priority', 'desc'));
      }

      return onSnapshot(
        q,
        (snapshot) => {
          console.log(`Received ${snapshot.docs.length} news tags`);
          const tags = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            // Helper function to convert Firestore Timestamp to Date
            const toDate = (timestamp: any): Date => {
              if (!timestamp) return new Date();
              if (timestamp instanceof Date) return timestamp;
              if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                return timestamp.toDate();
              }
              return new Date(timestamp);
            };

            return {
              id: docSnap.id,
              tag: data.tag || '',
              type: data.type || 'ACCUMULATIVE',
              description: data.description || '',
              rule: data.rule || '',
              priority: data.priority || 5,
              isActive: data.isActive !== undefined ? data.isActive : true,
              fromSentiment: data.fromSentiment,
              toSentiment: data.toSentiment,
              createdOn: toDate(data.createdOn),
              modifiedOn: toDate(data.modifiedOn)
            } as NewsTagInfo;
          });
          onData(tags);
        },
        (error) => {
          console.error('Error in news tags snapshot listener:', error);
          if (onError) {
            onError(error instanceof Error ? error : new Error(String(error)));
          }
        }
      );
    } catch (error) {
      console.error('Error in subscribeToNewsTags:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
      return () => { }; // Return empty unsubscribe function on error
    }
  };

  // Create a new news tag
  const createNewsTag = async (tagData: Omit<NewsTagInfo, 'id' | 'createdOn' | 'modifiedOn'>): Promise<boolean> => {
    try {
      console.log('Creating new news tag');
      const tagsRef = collection(db, COLL_NEWS_TAGS);
      await addDoc(tagsRef, {
        tag: tagData.tag,
        type: tagData.type,
        description: tagData.description,
        rule: tagData.rule,
        priority: tagData.priority || 5,
        isActive: tagData.isActive !== undefined ? tagData.isActive : true,
        fromSentiment: tagData.fromSentiment ?? 0,
        toSentiment: tagData.toSentiment ?? 0,
        createdOn: serverTimestamp(),
        modifiedOn: serverTimestamp()
      });
      console.log('News tag created successfully');
      return true;
    } catch (error) {
      console.error('Error creating news tag:', error);
      return false;
    }
  };

  // Update an existing news tag
  const updateNewsTag = async (tagId: string, updates: Partial<Omit<NewsTagInfo, 'id' | 'createdOn'>>): Promise<boolean> => {
    try {
      console.log(`Updating news tag ${tagId}`);
      const tagRef = doc(db, COLL_NEWS_TAGS, tagId);
      await updateDoc(tagRef, {
        ...updates,
        modifiedOn: serverTimestamp()
      });
      console.log('News tag updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating news tag:', error);
      return false;
    }
  };

  // Delete a news tag
  const deleteNewsTag = async (tagId: string): Promise<boolean> => {
    try {
      console.log(`Deleting news tag ${tagId}`);
      const tagRef = doc(db, COLL_NEWS_TAGS, tagId);
      await deleteDoc(tagRef);
      console.log('News tag deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting news tag:', error);
      return false;
    }
  };

  return {
    isInitialized,
    error,
    isSubscribed,
    clearBatchLogs,
    clearAllMarketNews,
    getMultipleMarketNewsSnapshot,
    getSecFilingsSnapshot,
    enrichUserInfo,
    confirmCustomer,
    processDocument,
    getTickersFromImage,
    refreshItemNews,
    addNews,
    addUserList,
    addItemToWatchlist,
    getItemBySymbol,
    cleanup,
    deleteList,
    getListsForUser,
    getLatestBatchId,
    getLatestPrimaryBatchId,
    getBatchRunInfo,
    getVisitors,
    getItemDoc,
    getNewsDoc,
    getNewsItemSnapshot,
    getItemsSnapshot,
    getListInfo,
    getUserByEmail,
    getUserById,
    getCustomerByEmail,
    getVisitorCountSnapshot,
    getListData,
    getListNewsInfo,
    getMultipleListsNewsInfo,
    getLatestMarketNewsSnapshot,
    getHttpsCallable,
    subscribeToNewsletter,
    saveUserRemote,
    updateVisitorCount,
    createCheckoutSession,
    getSubscriptionsSnapshot,
    getPaymentInfosSnapshot,
    querySnapshotToDataWithId,
    getListInfosForUser,
    generateFirestoreGraphText,
    getAllUsers,
    subscribeToUsers,
    deleteUser,
    triggerManualNewsletterSend,
    refreshUserEmailContent,
    verifyUserEmail,
    getAllBatchRuns,
    subscribeToBatchRuns,
    getListRunsForBatch,
    subscribeToListRunsForBatch,
    subscribeToBatchLogs,
    deleteBatchRun,
    resetBatch,
    processSingleBatch,
    processNextBatch,
    checkAndRunNextAvailableBatch,
    rebuildReservedLists,
    confirmReservedLists,
    updateListActiveStatus,
    enqueueReservedLists,
    enqueueAllUserLists,
    enqueueBatch,
    // triggerUserPrepBatch,
    getSystemRunSettings,
    updateSystemSettings,
    subscribeToSystemRunSettings,
    // Thresholds-related functions
    subscribeToThresholds,
    updateThresholds,
    subscribeToItemRunsForListRun,
    getNewsForSymbol,
    // Prompt-related functions
    getPrompts,
    subscribeToPrompts,
    updatePromptActiveStatus,
    createOrUpdatePrompt,
    deletePrompt,
    // News sources-related functions
    subscribeToNewsSources,
    createNewsSource,
    updateNewsSource,
    deleteNewsSource,
    // News tags-related functions
    subscribeToNewsTags,
    createNewsTag,
    updateNewsTag,
    deleteNewsTag
    // hasActiveSubscription
  }
}
